import { useCallback, useState } from "react";
import useUser from "../../hooks/context/userHook";
import useFreeMonthOffer from "../../hooks/api/useFreeMonthOffer";
import { MondayApiQueryInstance } from "../../services/monday/monday-service-ts";
import { analytics } from "../../utils/segment/analyticsService";
import OfferPanel, { OfferStep } from "./OfferPanel";
import "./FreeMonthOffer.css";

const closeModal = () => MondayApiQueryInstance.closeAppFeatureModal().catch(() => {});

/**
 * The `/free-month-offer` route: what monday's app feature modal shows when
 * FreeMonthOffer opens it. The backend still decides everything; this page
 * only claims or dismisses. Closing with monday's own X is turned into a
 * dismissal by FreeMonthOffer, which gets told when the modal closes.
 */
const FreeMonthOfferPage = () => {
  const { accountId } = useUser();
  const { status, accept, dismiss, refresh } = useFreeMonthOffer(true);

  const [step, setStep] = useState<OfferStep>("offer");
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState(false);
  const [validUntil, setValidUntil] = useState<string | null>(null);

  const track = useCallback(
    (event: string) => analytics.track(event, { groupId: `${accountId}`, offer_version: "v1" }),
    [accountId],
  );

  // After a successful accept the status stops offering, but the redeem step stays up.
  if (step !== "redeem" && !status?.showOffer) return null;

  const handleDismiss = async () => {
    await dismiss();
    closeModal();
  };

  const handleAccept = async () => {
    setAccepting(true);
    setAcceptError(false);
    try {
      const result = await accept();
      if (result.accepted) {
        setValidUntil(result.validUntil);
        setStep("redeem");
        refresh();
      } else if (result.state === "grant_failed") {
        setAcceptError(true);
      } else {
        // No longer claimable (e.g. the account subscribed meanwhile).
        closeModal();
      }
    } catch {
      setAcceptError(true);
    } finally {
      setAccepting(false);
    }
  };

  return (
    <OfferPanel
      step={step}
      accepting={accepting}
      acceptError={acceptError}
      validUntil={validUntil}
      onAccept={handleAccept}
      onDismiss={handleDismiss}
      onTrack={track}
    />
  );
};

export default FreeMonthOfferPage;
